import { Component, Input, OnInit, SimpleChanges, ViewChild, ViewEncapsulation, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { RcsService } from '../../../../../../_services/messaging/messages/rcs-messages/rcs.service';
import { ValidationService } from '../../../../../../_services/messaging/messages/rcs-messages/validation.service';
import { RcsMessageService } from '../../../../../../_services/messaging/messages/rcs-messages/rcs-message.service';
import { NotificationService } from '../../../../../../_services/notification.service';
import { RcsMessage, MessageType, RcsMessageConfig } from '../../../../../../_models/messaging/messages/rcs-messages/rcs-message.model';
import { MessageTypeEnum } from '../../../../../../_models/messaging/messages/message-type-enum';
import { RcsTextComponent } from '../rcs-text/rcs-text.component';
import { RcsRichCardComponent } from '../rcs-rich-card/rcs-rich-card.component';
import { RcsCarouselComponent } from '../rcs-carousel/rcs-carousel.component';
import { ExecutionResult } from '../../../../../../_models/execution-result-enum';
import { HelperService } from 'src/app/_services/system/helpers/helper.service';
import { LocalStorageService } from 'src/app/_services/system/localStorage/localStorage.service';

@Component({
  selector: 'app-message-builder',
  templateUrl: './message-builder.component.html',
  styleUrls: ['./message-builder.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class MessageBuilderComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  activeTab: MessageType = MessageType.SMS;
  currentMessage: RcsMessage | null = null;
  MessageType = MessageType;
  showJsonExport: boolean = false;
  jsonConfig: string = '';
  
  // Edit mode properties
  isReadOnly: boolean = false;
  isLoadingMessage: boolean = false;
  
  hasClientAnyRcsApp: boolean = false

  @Input() initialType?: MessageType;
  @Input() messageName: string;
  @Input() selectedFolderId: number;
  @Input() isEditMode: boolean = false;
  @Input() messageId: number = 0;
  @Input() messageData: any = null;
  
  @ViewChild(RcsTextComponent) rcsTextComponent!: RcsTextComponent;
  @ViewChild(RcsRichCardComponent) rcsRichCardComponent!: RcsRichCardComponent;
  @ViewChild(RcsCarouselComponent) rcsCarouselComponent!: RcsCarouselComponent;
  
  constructor(
    private rcsService: RcsService,
    private validationService: ValidationService,
    private route: ActivatedRoute,
    private router: Router,
    private rcsMessageService: RcsMessageService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private helperService: HelperService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.helperService.hasClientAnyRcsApplication(Number(this.localStorageService.getCurrentClientId)).subscribe(result => {
      this.hasClientAnyRcsApp = result;
    });

    // Use parameters passed from parent component if in edit mode
    if (this.isEditMode && this.messageId > 0) {
      this.isReadOnly = true; // Disable type switching in edit mode
      if (this.messageData) {
        this.loadMessageFromData();
      } else {
        this.loadMessageForEdit();
      }
    } else {
      // Check if we're in standalone edit mode (from route)
      this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
        const id = params.get('messageId');
        if (id) {
          this.messageId = parseInt(id, 10);
          this.isEditMode = true;
          this.isReadOnly = true; // Disable type switching in edit mode
          this.loadMessageForEdit();
        } else {
          // Creation mode
          this.isEditMode = false;
          this.isReadOnly = false;
          if (this.initialType !== undefined) {
            this.activeTab = this.initialType;
          }
        }
      });
    }

    this.rcsService.getCurrentMessage().pipe(takeUntil(this.destroy$)).subscribe(message => {
      this.currentMessage = message;
      // Only update activeTab from service if we're in edit mode or creation mode WITHOUT initialType
      if (message && !this.isEditMode && this.initialType === undefined) {
        // Only update activeTab if it's different to avoid unnecessary changes
        if (this.activeTab !== message.type) {
          this.activeTab = message.type;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMessageForEdit(): void {
    if (this.messageId > 0) {
      this.isLoadingMessage = true;
      this.rcsMessageService.get(this.messageId).pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.isLoadingMessage = false;
            if (result.executionResult === ExecutionResult.success && result.data) {
              const rcsMessage = result.data;
              this.messageName = rcsMessage.messageName || '';
              this.selectedFolderId = rcsMessage.folderID || 0;
              
              // Load message content based on type
              if (rcsMessage.rcsContentJson && typeof rcsMessage.rcsContentJson === 'string') {
                try {
                  const contentJson = JSON.parse(rcsMessage.rcsContentJson);
                  
                  // Determine message type and set active tab
                  let messageType = MessageType.TEXT;
                  switch (rcsMessage.rcsType?.toLowerCase()) {
                    case 'text':
                      messageType = MessageType.TEXT;
                      break;
                    case 'richcard':
                      messageType = MessageType.STANDALONE;
                      break;
                    case 'carousel':
                      messageType = MessageType.CAROUSEL;
                      break;
                    default:
                      messageType = MessageType.TEXT;
                  }

                  // Parse the RCS content based on type to pass to child component
                  let parsedContent: any = null;
                  if (rcsMessage.rcsType?.toLowerCase() === 'text') {
                    parsedContent = contentJson.text || '';
                  } else if (rcsMessage.rcsType?.toLowerCase() === 'richcard') {
                    parsedContent = contentJson.richCard || {};
                  } else if (rcsMessage.rcsType?.toLowerCase() === 'carousel') {
                    parsedContent = contentJson.carousel || {};
                  }

                  // BUILD currentMessage BEFORE changing activeTab, so the new component gets data via @Input
                  this.currentMessage = {
                    id: rcsMessage.messageId?.toString(),
                    type: messageType,
                    text: messageType === MessageType.TEXT ? parsedContent : undefined,
                    richCard: messageType === MessageType.STANDALONE ? parsedContent : undefined,
                    carousel: messageType === MessageType.CAROUSEL ? parsedContent : undefined,
                    suggestions: rcsMessage.suggestions || [],
                    fallbackText: rcsMessage.smsFallbackText || '',
                    fallbackSenderId: rcsMessage.smsFallbackClientInboxId ? rcsMessage.smsFallbackClientInboxId.toString() : undefined,
                    timestamp: rcsMessage.creationDate ? new Date(rcsMessage.creationDate) : new Date()
                  };

                  // NOW set the active tab - this will trigger *ngIf change and component creation
                  this.activeTab = messageType;

                  this.rcsService.updateMessage(this.currentMessage);

                  // Defer load into the active child so the view initializes
                  // currentMessage already set with parsed content, so child component will receive it via @Input
                  // and ngOnChanges will populate the form. No need for load() here!
                  
                  this.rcsService.updateMessage(this.currentMessage);
                } catch (e) {
                  console.warn('Failed to parse content JSON:', e);
                  this.activeTab = MessageType.TEXT;
                }
              } else {
                console.warn('[MessageBuilder] rcsContentJson is missing or not string:', typeof rcsMessage.rcsContentJson);
              }
            } else {
              this.notificationService.showError('Failed to load message');
              this.router.navigate(['/messages']);
            }
          },
          error: (error) => {
            this.isLoadingMessage = false;
            this.notificationService.showError('Error loading message');
            console.error('Error loading message:', error);
            this.router.navigate(['/messages']);
          }
        });
    }
  }

  loadMessageFromData(): void {
    if (this.messageData) {
      // Parse RCS content based on type
      let messageType = MessageType.TEXT;
      let parsedContent: any = null;
      let suggestions: any[] = [];
      
      // Set active tab based on message type
      switch (this.messageData.RCSType?.toLowerCase()) {
        case 'text':
          messageType = MessageType.TEXT;
          break;
        case 'richcard':
          messageType = MessageType.STANDALONE;
          break;
        case 'carousel':
          messageType = MessageType.CAROUSEL;
          break;
        default:
          messageType = MessageType.TEXT;
      }
      
      // Parse RCSContentJson
      if (this.messageData.RCSContentJson) {
        try {
          const contentJson = JSON.parse(this.messageData.RCSContentJson);
          
          if (messageType === MessageType.TEXT) {
            parsedContent = contentJson.text || '';
          } else if (messageType === MessageType.STANDALONE) {
            parsedContent = contentJson.richCard || {};
          } else if (messageType === MessageType.CAROUSEL) {
            parsedContent = contentJson.carousel || {};
          }
        } catch (e) {
          console.warn('Failed to parse RCSContentJson:', e);
        }
      }
      
      // Parse suggestions with scope handling
      if (this.messageData.Suggestions && Array.isArray(this.messageData.Suggestions)) {
        const globalSuggestions: any[] = [];

        this.messageData.Suggestions.forEach((s: any) => {
          const mapped = {
            type: s.Type || s.type,
            displayText: s.DisplayText || s.displayText,
            url: s.Url || s.url,
            phone: s.Phone || s.phone,
            address: s.Address || s.address,
            latitude: s.Latitude ?? s.latitude,
            longitude: s.Longitude ?? s.longitude,
            startTime: s.StartTime || s.startTime,
            endTime: s.EndTime || s.endTime,
            meetingTitle: s.MeetingTitle || s.meetingTitle,
            meetingDescription: s.MeetingDescription || s.meetingDescription
          } as any;

          const scope = (s.SuggestionScope || s.suggestionScope || '').toLowerCase();

          // For carousel, apply card-scoped suggestions directly to cards
          if (messageType === MessageType.CAROUSEL && scope === 'card') {
            const cardIndex = s.CardIndex ?? s.cardIndex;
            if (typeof cardIndex === 'number' && parsedContent && parsedContent.cards && parsedContent.cards[cardIndex]) {
              parsedContent.cards[cardIndex].suggestions = parsedContent.cards[cardIndex].suggestions || [];
              parsedContent.cards[cardIndex].suggestions.push(mapped);
            } else {
              // Fallback: if card index invalid, treat as global to avoid data loss
              globalSuggestions.push(mapped);
            }
          } else {
            // Text and rich-card use global suggestions; carousel global also goes here
            globalSuggestions.push(mapped);
          }
        });

        suggestions = globalSuggestions;
      }
      
      // Set active tab BEFORE building currentMessage
      this.activeTab = messageType;
      
      // Create RcsMessage object with parsed content
      const rcsMessage: RcsMessage = {
        id: this.messageData.MessageID?.toString() || this.messageData.messageId?.toString(),
        type: messageType,
        text: messageType === MessageType.TEXT ? parsedContent : undefined,
        richCard: messageType === MessageType.STANDALONE ? parsedContent : undefined,
        carousel: messageType === MessageType.CAROUSEL ? parsedContent : undefined,
        suggestions: suggestions.length > 0 ? suggestions : undefined,
        timestamp: this.messageData.CreationDate ? new Date(this.messageData.CreationDate) : new Date(),
        fallbackText: this.messageData.SMSFallbackText || '',
        fallbackSenderId: this.messageData.SMSFallbackClientInboxID ? this.messageData.SMSFallbackClientInboxID.toString() : undefined
      };
      
      this.currentMessage = rcsMessage;
      this.rcsService.updateMessage(rcsMessage);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialType'] && changes['initialType'].currentValue !== undefined) {
      const newType = changes['initialType'].currentValue;
      
      // Only switch if the type actually changed
      if (this.activeTab !== newType) {
        this.activeTab = newType;
        
        // Create a new message structure for the new type BEFORE activeTab is rendered
        this.currentMessage = {
          type: newType,
          text: newType === MessageType.TEXT ? '' : undefined,
          richCard: newType === MessageType.STANDALONE ? {
            title: '',
            description: '',
            orientation: 'VERTICAL',
            thumbnailAlignment: 'LEFT',
            media: { url: '', height: 'MEDIUM' },
            suggestions: []
          } : undefined,
          carousel: newType === MessageType.CAROUSEL ? {
            width: 'MEDIUM',
            cards: []
          } : undefined,
          suggestions: []
        };
      }
    }
  }

  switchTab(type: MessageType): void {
    // Disable type switching in edit mode
    if (this.isReadOnly) {
      return;
    }
    
    this.activeTab = type;
    if (this.currentMessage) {
      const updatedMessage: RcsMessage = {
        ...this.currentMessage,
        type
      };
      this.rcsService.updateMessage(updatedMessage);
    }
  }

  onMessageChange(message: RcsMessage): void {
    // Guard: only accept emissions that match the currently active type.
    // Child components destroyed by *ngIf may still fire one last emit (e.g. from
    // resetForm called in ngOnDestroy), which would overwrite currentMessage with a
    // stale type and desync the preview while a different type is active.
    if (message.type !== this.activeTab) { return; }
    this.currentMessage = message;
    this.rcsService.updateMessage(message);
  }

  save(): void {
    if (!this.messageName) {
      alert('Please enter a message name before saving.');
      return;
    }

    if (!this.selectedFolderId && !this.isEditMode) {
      alert('Please select a folder before saving.');
      return;
    }

    try {
      // Delegate to the appropriate child component based on activeTab
      switch (this.activeTab) {
        case MessageType.TEXT:
          if (this.rcsTextComponent) {
            const messageIdParam = this.isEditMode ? this.messageId : 0;
            this.rcsTextComponent.save(messageIdParam, this.messageName, this.selectedFolderId)
              .then((savedMessageId) => {
                // Only navigate to messages after successful save
                setTimeout(() => {
                  this.router.navigate(['/messages']);
                }, 1000);
              })
              .catch((error) => {
                // Stay on the form if there's an error - don't navigate
                console.error('Error saving RCS message:', error);
              });
          }
          break;
        case MessageType.STANDALONE:
          if (this.rcsRichCardComponent) {
            const messageIdParam = this.isEditMode ? this.messageId : 0;
            this.rcsRichCardComponent.save(messageIdParam, this.messageName, this.selectedFolderId)
              .then((savedMessageId) => {
                setTimeout(() => {
                  this.router.navigate(['/messages']);
                }, 1000);
              })
              .catch((error) => {
                console.error('Error saving RCS message:', error);
              });
          }
          break;
        case MessageType.CAROUSEL:
          if (this.rcsCarouselComponent) {
            const messageIdParam = this.isEditMode ? this.messageId : 0;
            this.rcsCarouselComponent.save(messageIdParam, this.messageName, this.selectedFolderId)
              .then((savedMessageId) => {
                setTimeout(() => {
                  this.router.navigate(['/messages']);
                }, 1000);
              })
              .catch((error) => {
                console.error('Error saving RCS message:', error);
              });
          }
          break;
        default:
          alert('Please select a valid message type before saving.');
          break;
      }
    } catch (error) {
      console.error('Error saving RCS message:', error);
      alert('An error occurred while saving the message. Please try again.');
    }
  }

  send(): void {
    // First save the message for send, then navigate to send page
    this.saveForSend().then(() => {
      this.navigateToSendPage();
      // Clear fields only for new messages after successful navigation
      if (!this.isEditMode) {
        this.clearFields();
      }
    }).catch(error => {
      console.error('Failed to save message before sending:', error);
    });
  }

  clearFields(): void {
    // Reset basic fields
    this.messageName = '';
    this.selectedFolderId = 0;
    this.messageId = 0;
    
    // Reset message type to default
    this.activeTab = MessageType.TEXT;
    
    // Clear child component data based on current active tab
    if (this.rcsTextComponent) {
      this.rcsTextComponent.resetForm();
    }
    if (this.rcsRichCardComponent) {
      this.rcsRichCardComponent.resetForm();
    }
    if (this.rcsCarouselComponent) {
      this.rcsCarouselComponent.resetForm();
    }
    
    // Reset current message
    this.currentMessage = {
      type: MessageType.TEXT,
      text: '',
      suggestions: []
    };
    
    // Update the service with clear message
    this.rcsService.updateMessage(this.currentMessage);
  }

  saveForSend(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.messageName) {
        alert('Please enter a message name before saving.');
        reject(new Error('Message name required'));
        return;
      }

      if (!this.selectedFolderId && !this.isEditMode) {
        alert('Please select a folder before saving.');
        reject(new Error('Folder selection required'));
        return;
      }

      try {
        // Delegate to the appropriate child component based on activeTab
        switch (this.activeTab) {
          case MessageType.TEXT:
            if (this.rcsTextComponent) {
              const messageIdParam = this.isEditMode ? this.messageId : 0;
              this.rcsTextComponent.save(messageIdParam, this.messageName, this.selectedFolderId)
                .then((savedMessageId) => {
                  if (!this.isEditMode && savedMessageId) {
                    this.messageId = savedMessageId;
                  }
                  resolve();
                })
                .catch(reject);
            } else {
              reject(new Error('RCS Text component not available'));
            }
            break;
          case MessageType.STANDALONE:
            if (this.rcsRichCardComponent) {
              const messageIdParam = this.isEditMode ? this.messageId : 0;
              this.rcsRichCardComponent.save(messageIdParam, this.messageName, this.selectedFolderId)
                .then((savedMessageId) => {
                  if (!this.isEditMode && savedMessageId) {
                    this.messageId = savedMessageId;
                  }
                  resolve();
                })
                .catch(reject);
            } else {
              reject(new Error('RCS Rich Card component not available'));
            }
            break;
          case MessageType.CAROUSEL:
            if (this.rcsCarouselComponent) {
              const messageIdParam = this.isEditMode ? this.messageId : 0;
              this.rcsCarouselComponent.save(messageIdParam, this.messageName, this.selectedFolderId)
                .then((savedMessageId) => {
                  if (!this.isEditMode && savedMessageId) {
                    this.messageId = savedMessageId;
                  }
                  resolve();
                })
                .catch(reject);
            } else {
              reject(new Error('RCS Carousel component not available'));
            }
            break;
          default:
            alert('Please select a valid message type before saving.');
            reject(new Error('Invalid message type'));
            break;
        }
      } catch (error) {
        console.error('Error saving RCS message:', error);
        alert('An error occurred while saving the message. Please try again.');
        reject(error);
      }
    });
  }

  navigateToSendPage(): void {
    if (!this.messageId) {
      console.error('No message ID available for navigation');
      return;
    }

    let messageTypeEnum: MessageTypeEnum;

    // Determine the specific RCS type based on current message type
    switch (this.currentMessage?.type) {
      case MessageType.TEXT:
        messageTypeEnum = MessageTypeEnum['rcs-text'];
        break;
      case MessageType.STANDALONE:
        messageTypeEnum = MessageTypeEnum['rcs-rich-card'];
        break;
      case MessageType.CAROUSEL:
        messageTypeEnum = MessageTypeEnum['rcs-carousel'];
        break;
      default:
        messageTypeEnum = MessageTypeEnum['rcs-text'];
    }

    this.router.navigate(['messages/message-send', this.messageId, messageTypeEnum]);
  }


  exportJson(): void {
    // Use the current message - it should be up to date from the subscription
    if (!this.currentMessage) {
      alert('No message configuration to export. Please configure a message first.');
      return;
    }

    // Validate that we have the required data based on message type
    if (this.currentMessage.type === MessageType.SMS) {
      if (!this.currentMessage.smsText) {
        alert('Please enter SMS text content before exporting.');
        return;
      }
      const textCheck = this.validationService.validateTextForProfanity(this.currentMessage.smsText);
      if (!textCheck.valid) {
        alert('Cannot export: ' + textCheck.error);
        return;
      }
    }

    if (this.currentMessage.type === MessageType.TEXT && !this.currentMessage.text) {
      alert('Please enter text content before exporting.');
      return;
    }

    if (this.currentMessage.type === MessageType.STANDALONE && !this.currentMessage.richCard) {
      alert('Please configure the rich card before exporting.');
      return;
    }

    if (this.currentMessage.type === MessageType.CAROUSEL && (!this.currentMessage.carousel || !this.currentMessage.carousel.cards || this.currentMessage.carousel.cards.length === 0)) {
      alert('Please add at least one card to the carousel before exporting.');
      return;
    }

    // Additional validation: Check for profanity in text fields and validate URLs
    if (this.currentMessage.type === MessageType.TEXT && this.currentMessage.text) {
      const textCheck = this.validationService.validateTextForProfanity(this.currentMessage.text);
      if (!textCheck.valid) {
        alert('Cannot export: ' + textCheck.error);
        return;
      }
    }

    if (this.currentMessage.type === MessageType.STANDALONE && this.currentMessage.richCard) {
      if (this.currentMessage.richCard.title) {
        const titleCheck = this.validationService.validateTextForProfanity(this.currentMessage.richCard.title);
        if (!titleCheck.valid) {
          alert('Cannot export: Card title contains inappropriate language.');
          return;
        }
      }
      if (this.currentMessage.richCard.description) {
        const descCheck = this.validationService.validateTextForProfanity(this.currentMessage.richCard.description);
        if (!descCheck.valid) {
          alert('Cannot export: Card description contains inappropriate language.');
          return;
        }
      }
      if (this.currentMessage.richCard.media?.url) {
        const urlCheck = this.validationService.validateUrl(this.currentMessage.richCard.media.url);
        if (!urlCheck.valid) {
          alert('Cannot export: Media URL - ' + urlCheck.error);
          return;
        }
      }
      if (this.currentMessage.richCard.thumbnail?.url) {
        const urlCheck = this.validationService.validateUrl(this.currentMessage.richCard.thumbnail.url);
        if (!urlCheck.valid) {
          alert('Cannot export: Thumbnail URL - ' + urlCheck.error);
          return;
        }
      }
    }

    if (this.currentMessage.type === MessageType.CAROUSEL && this.currentMessage.carousel) {
      for (const card of this.currentMessage.carousel.cards) {
        if (card.title) {
          const titleCheck = this.validationService.validateTextForProfanity(card.title);
          if (!titleCheck.valid) {
            alert(`Cannot export: Card title contains inappropriate language.`);
            return;
          }
        }
        if (card.description) {
          const descCheck = this.validationService.validateTextForProfanity(card.description);
          if (!descCheck.valid) {
            alert(`Cannot export: Card description contains inappropriate language.`);
            return;
          }
        }
        if (card.media?.url) {
          const urlCheck = this.validationService.validateUrl(card.media.url);
          if (!urlCheck.valid) {
            alert('Cannot export: Card media URL - ' + urlCheck.error);
            return;
          }
        }
      }
    }

    try {
      const config = this.rcsService.generateConfig(this.currentMessage);
      this.jsonConfig = JSON.stringify(config, null, 2);
      this.showJsonExport = true;
    } catch (error) {
      console.error('Error generating config:', error);
      alert('An error occurred while generating the JSON configuration. Please check the console for details.');
    }
  }

  copyToClipboard(): void {
    if (this.jsonConfig) {
      navigator.clipboard.writeText(this.jsonConfig).then(() => {
        alert('JSON copied to clipboard!');
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  }

  closeJsonExport(): void {
    this.showJsonExport = false;
  }
}
